import { CreatePointRequest, CreatePointsResponse, FeatureType, GeocodeType, GptPlace, ProcessGptPlacesRequest, SuggestGptPlaces, TopicCategory, TtmGptJsonGeneralResponse } from '../../types';
import { LitElement, css, html } from 'lit';
import { addSuggestedPointsToMap, processSuggestedPoints, promptToInitializedMapAI } from '../../helpers/api/gpt-api';
import { customElement, state } from 'lit/decorators';
import { getFirstIcon, getFromLocalStorage, getMarkerElement, isDefined, setMarkerMap, undefinedIfEmpty } from '../../helpers/utils';

import { DEFAULT_CATEGORY_ICON } from '../../helpers/constants';
import { Marker } from '@twitchaxorg/markerclusterer';
import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import featuresInfo from '../../helpers/state/features-info';
import { getMainPage } from '../../helpers/page-name';
import { iterateAICount } from '../../helpers/api/account-api';
import pointCache from '../../helpers/state/point-cache';
import { suggestPointsOnboarding } from '../../helpers/api/points-api';
import topicData from '../../helpers/state/topic-data';

@customElement('admin-ai-point-generation-fragment')
export class AdminAddonsAiPointsFragment extends LitElement {
    private readonly amplitude_callsite: string = 'Addons AI Fragment';

    @state()
    usageMode: string = 'prompt';

    @state()
    view: 'enterPrompt' | 'processingPrompt' | 'selectPoints' | 'addingPoints' | 'success' = 'enterPrompt';

    @state()
    input_text: string = '';

    @state()
    result: CreatePointsResponse | undefined = undefined;

    @state()
    image_url: string | undefined = undefined;

    @state()
    map_data: TtmGptJsonGeneralResponse | undefined = undefined;

    @state()
    prompt_text: string = '';

    @state()
    suggested_points: GptPlace[] | CreatePointRequest[] = [];

    @state()
    selected_points: GptPlace[] | CreatePointRequest[] = [];

    @state()
    temp_points_icons: Map<string, Marker> = new Map();

    @state()
    temp_categories: string[] = [];

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindTopicState = new StateController(this, topicData);

    private changePreviewType(e: CustomEvent | any) {
        if (e.detail.tabIndex === 1) {
            this.usageMode = 'text';
            this.image_url = undefined;
            this.prompt_text = '';
        } else if (e.detail.tabIndex === 2) {
            this.usageMode = 'image';
            this.prompt_text = '';
            this.input_text = '';
        } else if (e.detail.tabIndex === 0) {
            this.usageMode = 'prompt';
            this.image_url = undefined;
            this.input_text = '';
        }
    }

    private onUploadCustom(e: CustomEvent) {
        if (e.detail?.imageUrl === undefined) {
            return;
        }
        this.image_url = e.detail.imageUrl;
    }

     async onChangeInputText(e: Event) {
        this.input_text = (e.target as HTMLInputElement).value;
    }

    async onChangePromptText(e: Event) {
        this.prompt_text = (e.target as HTMLInputElement).value;
    }

    async generatePoints() {
        this.view = 'processingPrompt';
        try {
            if (this.usageMode === 'text') {
                const suggestion_content: SuggestGptPlaces = {
                    location: this.map_data?.suggested_map_focus_area ?? '',
                    creation_method: {
                        data: [{
                            data: this.input_text,
                            data_type: 'text',
                        }]
                    },
                    max_points: accountInfo.isAuthenticated ? 50 : 10

                };
                await processSuggestedPoints(this.amplitude_callsite, suggestion_content, undefinedIfEmpty(accountInfo.account?.email) ?? getFromLocalStorage('email') ?? '', topicData.topic?._id.$oid, topicData.topicKey).then((response) => {
                    this.suggested_points = [...response.places];
                    this.selected_points = [...response.places];
                    this.map_data = response.general;
                    this.temp_categories = response.general.new_categories ?? [];
                    this.view = 'selectPoints';
                });
            } else if (this.usageMode === 'image' && this.image_url !== undefined) {
                // if image url starts with /i/ then it is a local image and we need to add the full url
                if (this.image_url.startsWith('/i/')) {
                    this.image_url = `${window.location.origin}${this.image_url}`;
                }
                const suggestion_content: SuggestGptPlaces = {
                    location: this.map_data?.suggested_map_focus_area ?? '',
                    creation_method: {
                        data: [{
                            data: this.image_url,
                            data_type: 'image_url',
                        }]
                    },
                    max_points: accountInfo.isAuthenticated ? 50 : 10
                };
                await processSuggestedPoints(this.amplitude_callsite, suggestion_content, undefinedIfEmpty(accountInfo.account?.email) ?? getFromLocalStorage('email') ?? '', topicData.topic?._id.$oid, topicData.topicKey).then((response) => {
                    this.suggested_points = [...response.places];
                    this.selected_points = [...response.places];
                    this.temp_categories = response.general.new_categories ?? [];
                    this.map_data = response.general;
                    this.view = 'selectPoints';
                });
            } else if (this.usageMode === 'prompt') {
                const result  = await promptToInitializedMapAI(this.amplitude_callsite, this.prompt_text, undefinedIfEmpty(accountInfo.account?.email) ?? getFromLocalStorage('email') ?? '', this.map_data?.suggested_map_focus_area, topicData.topicId, topicData.topicKey);
                this.temp_categories = result.new_categories ?? [];

                // add new categories to map
                const new_categories = this.temp_categories;
                const category_elements: TopicCategory[] = [];
                for (const category of new_categories) {
                    if (!topicData.categories.find((existingCategory) => existingCategory.name === category) && !category_elements.find((newCategory) => newCategory.name === category)) {
                        const new_category: TopicCategory = {
                            name: category,
                            icon: await getFirstIcon(this.amplitude_callsite, category) ?? DEFAULT_CATEGORY_ICON,
                            description: '',
                            _id: undefined,
                            icon_size: undefined,
                            can_crowdsource: true,
                        };
                        category_elements.push(new_category);
                    }
                }

                if (category_elements.length > 0) {
                    await topicData.createNewCategories(category_elements, this.amplitude_callsite);
                }

                const viewport = pointCache.map?.getBounds();
                if (viewport) {
                    const search_viewport = {
                        low: {
                            lat: viewport.getSouthWest().lat(),
                            lng: viewport.getSouthWest().lng(),
                        },
                        high: {
                            lat: viewport.getNorthEast().lat(),
                            lng: viewport.getNorthEast().lng(),
                        }
                    };
                   const points = await suggestPointsOnboarding(this.amplitude_callsite, search_viewport, this.temp_categories);
                   // put points in suggested points
                    this.suggested_points = points;
                    this.selected_points = [...this.suggested_points];
                    this.view = 'selectPoints';
                } else {
                    getMainPage().errorToastElement.showToastAndThrow('An error occurred while processing the input. Please try again later or contact support.');
                    this.view = 'enterPrompt';
                }
            }
            // add points temporarily to map
            this.suggested_points?.forEach((place, index) => {
                if (!('location' in place) || !place.location.latLng) {
                    return;
                }
                setTimeout(() => {
                    const icon  = {
                        url: 'https://img.icons8.com/?size=48&id=19326&format=png',
                        scaledSize: new google.maps.Size(30, 30),
                        anchor: new google.maps.Point(15, 15),
                    };
                    const marker = getMarkerElement(topicData.mapComponent?.googleMapComponent.map, place.location.latLng, icon, 500, pointCache.advancedMarkers, undefined, place.location.place_id);

                    setMarkerMap(marker, topicData.mapComponent?.googleMapComponent.map);

                    this.temp_points_icons.set(place.location.place_id ?? index.toString(), marker);
                }, index * 200); // 200ms delay between each marker
            });
        } catch (e) {
            getMainPage().errorToastElement.showToastAndThrow('An error occurred while processing the input. Please try again later or contact support.');
            this.view = 'enterPrompt';
        }
    }

    async saveGptPlacePoints(places: GptPlace[]) {
        this.view = 'addingPoints';
        // first see if we need to create any new categories - check if they are in temp_categories and a selected point is in that category
        // if so, create the category
        const new_categories = this.temp_categories;
        const category_elements: TopicCategory[] = [];

        for (const point of places) {
            if (new_categories.find((category) => category === point.category_name) && !topicData.categories.find((category) => category.name === point.category_name) && !category_elements.find((newCategory) => newCategory.name === point.category_name)) {
                const new_category: TopicCategory = {
                    name: point.category_name,
                    icon: await getFirstIcon(this.amplitude_callsite, point.category_name) ?? DEFAULT_CATEGORY_ICON,
                    description: '',
                    _id: undefined,
                    icon_size: undefined,
                    can_crowdsource: true,
                };
                category_elements.push(new_category);
            }
        }

        if (category_elements.length > 0) {
            // create the categories
            await topicData.createNewCategories(category_elements, this.amplitude_callsite);
        }
        try {
            // now update the points
            if (this.selected_points.length > 0 && topicData.topic !== undefined) {

                const points :ProcessGptPlacesRequest =  {
                    places,
                    topic_id: topicData.topic?._id.$oid,
                    location: this.map_data?.suggested_map_focus_area
                };

                const response = await addSuggestedPointsToMap(this.amplitude_callsite, points, topicData.topicKey);
                this.view = 'success';
                this.result = response;
            }

            await pointCache.refreshPoints(false);

            await iterateAICount(this.amplitude_callsite);
        } catch (e) {
            getMainPage().errorToastElement.showToastAndThrow('An error occurred while saving the points. Please try again later or contact support.');
            this.view = 'enterPrompt';
        }
    }

    async saveNearbyPoints(places: CreatePointRequest[]) {
        this.view = 'addingPoints';

        // remove all temporary markers from the map
        this.temp_points_icons.forEach((marker) => {
            setMarkerMap(marker, null);
        });

        const results = await pointCache.fetchAndAddMany(places, GeocodeType.Place);
        this.result = results;
        await pointCache.refreshPoints(false);

        this.view = 'success';

        await iterateAICount(this.amplitude_callsite);
    }

    private async toggleSelection(point: any) {
        const foundIndex = this.selected_points?.findIndex((p) => p.name === point.name);
        if (foundIndex > -1) {
            this.selected_points.splice(foundIndex, 1);
            // remove the marker from the map
            const icon = this.temp_points_icons.get(point.location.place_id ?? foundIndex.toString());
            if (icon) {
                setMarkerMap(icon, null);
            }
        } else {
            this.selected_points.push(point);
            const newIndex = this.selected_points.length - 1;
            const markerKey = point.location.place_id ?? newIndex.toString();
            // add the marker to the map
            if (this.temp_points_icons.get(markerKey) === undefined) {
                const icon = getMarkerElement(topicData.mapComponent?.googleMapComponent.map, point.location.latLng, {
                    url: 'https://img.icons8.com/?size=48&id=19326&format=png',
                    scaledSize: new google.maps.Size(30, 30),
                    anchor: new google.maps.Point(15, 15),
                }, 500, pointCache.advancedMarkers, undefined, point.location.place_id);
                setMarkerMap(icon, topicData.mapComponent?.googleMapComponent.map);
                this.temp_points_icons.set(markerKey, icon);
            }
        }
    }

    openUpgradeDrawerAiUses() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.NumberOfUsesOfProxiAI,
                },
            })
        );
    }

    openUpgradeDrawerPoints() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.PointsPerMap,
                },
            })
        );
    }


    protected render() {
        const uses = accountInfo.ai_tool_uses_this_month ?? parseInt(getFromLocalStorage('ai_tool_uses_this_month') ?? '0');
        return html`
            <div class="addon__container">
                ${this.image_url !== undefined ? html`
                    <img src="${this.image_url}" alt="Uploaded Image" style="max-width: 100%; max-height: 200px;">
                ` : ''}
                ${this.renderPage()}
                <br><br>
                You have used ${uses} of ${featuresInfo.numberOfUsesOfProxiAIEnabled ?? 3} AI uses this month.
                ${uses >= (featuresInfo.numberOfUsesOfProxiAIEnabled ?? 3) ? html`
                    <br><span @click=${this.openUpgradeDrawerAiUses} style="cursor: pointer;text-decoration:underline">Upgrade for More</span>
                ` : ''}
                <br><br>
                ${pointCache.pointsRemaining <= 0 ? html`
                    <span>You have reached the maximum number of points for this topic.
                    <br><span @click=${this.openUpgradeDrawerPoints} style="cursor: pointer;text-decoration:underline">Upgrade for More</span>
                ` : ''}
            </div>
        `;
    }

    protected renderPage() {
        if (this.view === 'enterPrompt') {
            return this.renderPromptOptions();
        } else if (this.view === 'processingPrompt') {
            return html`<div>
                    <loading-element
                    loadingString="Processing Prompt..."
                    style="margin-bottom: 10px"
                >
                </loading-element></div>`;
        } else if (this.view === 'selectPoints') {
            return this.renderPointSelection();
        } else if (this.view === 'addingPoints') {
            return html`
                <div>
                    <loading-element
                        loadingString="Adding Points..."
                        style="margin-bottom: 10px"
                    >
                    </loading-element>
                </div>
            `;
        } else if (this.view === 'success') {
            return this.renderSuccess();
        }
    }

    updatePointCategory(point: GptPlace | CreatePointRequest, index: number) {
        if (this.isGptPlace(point)) {
            const category_name = (this.shadowRoot?.getElementById(`category_${index}`) as HTMLInputElement).value;
            point.category_name = category_name;
            // update the selected points
            const selected_index = this.selected_points.findIndex((p) => p.name === point.name);
            if (index > -1) {
                this.selected_points[selected_index] = point;
            }
        } else {
            const category_name = (this.shadowRoot?.getElementById(`category_${index}`) as HTMLInputElement).value;
            point.topic_category_id = topicData.categories.find((category) => category.name === category_name)?._id ?? { $oid: '' };
            // update the selected points
            const selected_index = this.selected_points.findIndex((p) => p.name === point.name);
            if (index > -1) {
                this.selected_points[selected_index] = point;
            }
        }
    }

    renderPromptOptions() {
        const uses = accountInfo.ai_tool_uses_this_month ?? parseInt(getFromLocalStorage('ai_tool_uses_this_month') ?? '0');
        return html`
            <tabs-component
                id="tabsComponent"
                type="pill"
                .options=${['Prompt', 'Text', 'Image']}
                @on-select-tab=${(e: CustomEvent) => {
                    this.changePreviewType(e);
                }}
            ></tabs-component>
            <br><br>
            ${this.usageMode === 'text'
                ? html`
                      <proxi-textarea
                            id="textPromptComponent"
                            label="Paste or type your text here"
                            outlined
                            rows="4"
                            cols="40"
                            @input=${this.onChangeInputText}
                            charCounter='internal'
                            maxlength="10000"
                        ></proxi-textarea>


                  `
                : html``}
            ${this.usageMode === 'image'
                ? html`
                    <image-upload-component
                        autoUpload
                        .imageUrl="${this.image_url}"
                        @retreived-autoupload-url="${this.onUploadCustom}"
                    ></image-upload-component>
                  `
                : html``}

            ${this.usageMode === 'prompt'
                ? html`
                    <proxi-textarea
                        id="promptComponent"
                        label="Enter your prompt here"
                        outlined
                        rows="4"
                        cols="40"
                        @input=${this.onChangePromptText}
                        charCounter='internal'
                        maxlength="1000"
                    ></proxi-textarea>
                  `
                : html``}

            <form-button
                id="nextButton"
                .text=${'Next'}
                ?disabled=${(this.input_text === '' && this.image_url === undefined && this.prompt_text === '') || pointCache.pointsRemaining <= 0 || (uses) >= (featuresInfo.numberOfUsesOfProxiAIEnabled ?? 3)}
                @click=${this.generatePoints}
            > ${this.usageMode === 'text' ? 'Find Points from Text' : this.usageMode === 'image' ? 'Find Points in Image' : 'Suggest Points from Prompt'}</form-button>
            <p> Note: This tool is in beta and may not work perfectly. If you have any issues please contact support.</p>
            <p> Note: The tool will suggest up to ${accountInfo.isAuthenticated ? 50 : 10} points per call. You can select which points to add to the map.</p>
        `;
    }

    renderPointSelection() {
        // create combined list of existing categories and new categories
        const allCategories = topicData.categories.map((category) => category.name).concat(this.temp_categories);
        // remove duplicates
        const uniqueCategories = [...new Set(allCategories)];
        const isGptPlace = this.isGptPlace(this.suggested_points[0]);
        return html`
            <div>
                <h3>Select the points you would like to add to the map</h3>
                <div>
                    ${this.suggested_points.map((point, index) => {
                        const selected = isGptPlace ? (this.selected_points as GptPlace[]).find((p) => p.name === point.name) : (this.selected_points as CreatePointRequest[]).find((p) => p.name === point.name);
                        const selectedCategory = isGptPlace ?
                            uniqueCategories?.find((category) => category === (selected as GptPlace)?.category_name) ?? uniqueCategories[0] :
                            topicData.categories.find((category) => category.name === (selected as CreatePointRequest)?.topic_category_id.$oid)?.name ?? uniqueCategories[0];
                        return html`
                            <div class="list-item">
                                <div class="select-status">
                                    <form-checkbox
                                        .checked=${selected !== undefined}
                                        @change=${async () => await this.toggleSelection(point)}
                                    ></form-checkbox>
                                </div>
                                <div class="point-info">
                                    <span class="place-name">${point.name}</span>
                                    <proxi-selectfield-inline
                                        id="category_${index}"
                                        outlined
                                        class="input reward-criteria"
                                        @selected=${() => this.updatePointCategory(point, index)}
                                        value=${selectedCategory}
                                    >
                                        ${uniqueCategories?.map((category, index) => {
                                            return html`
                                                <mwc-list-item value="${category}">${category}</mwc-list-item>
                                            `;
                                        }
                                        )}
                                    </proxi-selectfield-inline>
                                </div>
                            </div>
                        `;
                    })}
                </div>
                <form-button
                    id="saveButton"
                    .text=${'Save'}
                    @click=${this.isGptPlace(this.suggested_points[0]) ? async () => await this.saveGptPlacePoints(this.selected_points as GptPlace[]) : async () => await this.saveNearbyPoints(this.selected_points as CreatePointRequest[])}
                >Save Points To Map</form-button>
            </div>
        `;
    }

    renderSuccess() {
        return html`
            <div>
                <h3>${this.result?.updated_point_ids.length} points added successfully</h3>
                ${this.result?.failed_validations && this.result?.failed_validations.length > 0 ? html`
                    <h4>Failed Validations</h4>
                    <ul>
                        ${this.result?.failed_validations.map((validation) => {
                            return html`
                                <li>${validation.point_request.name}: ${validation.message}</li>
                            `;
                        })}
                    </ul>
                ` : '' }

                ${this.result?.failed_geocodes && this.result?.failed_geocodes.length > 0 ? html`
                    <h4>Failed Geocodes</h4>
                    <ul>
                        ${this.result?.failed_geocodes.map((geocode) => {
                            return html`
                                <li>${geocode.name}: Geocode Failed - Could not find location</li>
                            `;
                        })}
                    </ul>
                ` : '' }
            </div>
            <form-button
                id="nextButton"
                .text=${'Close'}
                @click=${() => {
                    this.view = 'enterPrompt';
                    this.input_text = '';
                    this.result = undefined;
                    this.usageMode = 'text';
                }}
            >Back to Start</form-button>
        `;
    }

    isGptPlace(obj: any): obj is GptPlace {
        return isDefined(obj) && typeof obj.name === 'string' && typeof obj.category_name === 'string';
    }


    static get styles() {
        return css`
            /* width */
            .addon__container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .addon__container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .addon__container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .tabs {
                display: flex;
                cursor: pointer;
            }
            .tab {
                padding: 5px 10px;
                flex: 1;
                text-align: center;
            }
            .tab.active {
                border-bottom: 2px solid var(--main-secondary-color);
                font-weight: bold;
            }
            .tab-content {
                border: 1px solid #ccc;
                padding: 10px;
            }

            .addon__container {
                overflow: hidden auto;
                padding-right: 20px;
            }

            .list-item {
                display: flex;
                align-items: center;
                flex-direction: row;
                padding: 10px;
                border-bottom: 1px solid #ccc;
            }

            .addons__wrapper {
                display: flex;
                flex-wrap: wrap;
                justify-content: left;
            }

            .addon__preview-title {
                margin: 0;
                font-size: 0.7rem;
                font-weight: 700;
                cursor: pointer;
            }

            .addon__preview-supporting-text {
                font-size: 0.65rem;
                margin-top: 2px;
                margin-bottom: 0;
                color: var(--theme-input-color);
            }

            .addon__preview-title-wrapper {
                display: flex;
                align-items: center;
            }

            .addon__preview-image-wrapper-active {
                width: 30%;
                hover: pointer;
            }

            .addon_icon-holder {
                width: 0;
            }

            .addon__preview-image-wrapper {
                height: 50px;
                display: flex;
                justify-content: center;
            }

            .addon__preview-details-wrapper-inactive {
                width: 30%;
                flex-grow: 1;
                margin-left: 10px;
            }

            .addon__preview {
                padding: 10px 0;
                display: flex;
                align-items: center;
                width: 100%;
                justify-content: space-between;
                cursor: pointer;
            }

            .addon__icon {
                position: relative;
                left: 17px;
                top: 17px;
                --mdc-icon-size: 16px;
            }

            .addon__preview-image {
                height: 40px;
                margin-top: 20px;
                position: absolute;
                cursor: pointer;
            }

            .addon__preview-image-background {
                height: 100%
            }

            .addon__preview-upgrade-icon {
                height: 26px;
            }

            .addon__preview--active {
                max-width: 25%;
                padding: 10px;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                font-size: 0.7rem;

            }
        `;
    }
}
